/**
 * Flickity carousel initialization and lifecycle management hook
 * Handles Flickity instance creation, resize handling, and portal rendering for carousel controls
 *
 * Mirrors the viewport gate in <x-carousel>: Flickity promotes its slider track to a
 * compositor layer, so instances are only kept alive near the viewport. Consumers must
 * use the returned `inViewRange` to render real content when no instance exists, rather
 * than waiting on `isReady`.
 */

import Flickity from 'flickity'
import { ComponentChildren } from 'preact'
import { createPortal } from 'preact/compat'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import debounce from 'lodash.debounce'

const defaultOptions: Flickity.Options = {
  contain: true,
  resize: false
}

const IN_RANGE_MARGIN = '600px'
const OUT_OF_RANGE_MARGIN = '1500px'

const resizeListener = (flickity: Flickity) => {
  const minHeightElements = flickity.slider.querySelectorAll('.min-h-full')
  minHeightElements.forEach((element: Element) => {
    element.classList.remove('min-h-full')
  })
  flickity.resize()
  flickity.reloadCells()
  minHeightElements.forEach((element: Element) => {
    element.classList.add('min-h-full')
  })
}

export function useFlickity(target?: Element | null, options: Flickity.Options = {}, deps: any[] = []) {
  const flickityRef = useRef<Flickity | null>(null)
  const sliderRef = useRef<Element | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [inViewRange, setInViewRange] = useState(false)
  const flickityOptions = { ...defaultOptions, ...options }

  useEffect(() => {
    if (!target) return

    if (!('IntersectionObserver' in window)) {
      setInViewRange(true)
      return
    }

    const inViewObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setInViewRange(true)
      },
      { rootMargin: IN_RANGE_MARGIN }
    )

    const outOfViewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) setInViewRange(false)
      },
      { rootMargin: OUT_OF_RANGE_MARGIN }
    )

    inViewObserver.observe(target)
    outOfViewObserver.observe(target)

    return () => {
      inViewObserver.disconnect()
      outOfViewObserver.disconnect()
    }
  }, [target])

  useEffect(() => {
    if (!target || !inViewRange) return

    let cancelled = false
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null
    let initFrame: number | null = null
    let readyFrame1: number | null = null
    let readyFrame2: number | null = null
    setIsReady(false)

    const handleResize = debounce(() => {
      if (flickityRef.current) {
        resizeListener(flickityRef.current)
      }
    }, 100)

    initFrame = requestAnimationFrame(() => {
      initFrame = null
      if (cancelled) return

      const computedHeight = target.getBoundingClientRect().height
      if (computedHeight > 0) {
        (target as HTMLElement).style.minHeight = `${computedHeight}px`
      }

      flickityRef.current = new Flickity(target, { ...flickityOptions })
      ;(target as any).flickity = flickityRef.current

      let readyFired = false
      const handleReady = () => {
        if (cancelled || readyFired) return
        readyFired = true
        readyFrame1 = requestAnimationFrame(() => {
          readyFrame1 = null
          readyFrame2 = requestAnimationFrame(() => {
            readyFrame2 = null
            if (cancelled) return
            if ((target as HTMLElement).style.minHeight) {
              (target as HTMLElement).style.minHeight = ''
            }
            setIsReady(true)
          })
        })
      }

      flickityRef.current.on('ready', handleReady)
      fallbackTimer = setTimeout(handleReady, 100)
      window.addEventListener('resize', handleResize)
      sliderRef.current = target.querySelector('.flickity-slider')
    })

    return () => {
      cancelled = true
      if (initFrame !== null) cancelAnimationFrame(initFrame)
      if (readyFrame1 !== null) cancelAnimationFrame(readyFrame1)
      if (readyFrame2 !== null) cancelAnimationFrame(readyFrame2)
      if (fallbackTimer) clearTimeout(fallbackTimer)
      handleResize.cancel()
      if (target && (target as any).flickity === flickityRef.current) {
        delete (target as any).flickity
      }
      flickityRef.current?.destroy()
      flickityRef.current = null
      window.removeEventListener('resize', handleResize)
      setIsReady(false)
    }
  }, [target, inViewRange, ...deps])

  const SliderPortal = useCallback(
    ({ children }: {children: ComponentChildren}) => {
      if (!target) return children
      return createPortal(children, target)
    },
    [target]
  )

  return {
    flickity: flickityRef.current,
    SliderPortal,
    isReady,
    inViewRange
  }
}
